import { useEffect, useState } from "react";
import {
    fetchOccurrencesByGroupIndicators,
    IInProgressIndicators
} from "../api/fetchIndicators";
import { getOccurrenceStepLabelByStepValue, OccurrenceStepEnum, OccurrenceStepLabelEnum } from "../enums/occurrenceStep";
import { fetchOccurenceListWithTimeSpentForIndicators } from "../api/fetchOccurence";
import useAssets from "./useAssets";
import { OccurrenceWithTimeSpent } from "../models/occurrence";
import { getChassisListFromAssetIds } from "../utils/assetUtils";

function useIndicators(persona: 'TOWER' | 'MANAGER' | 'CONSULTANT' | 'PARTNERSHIP', setChassisList: (chassisList: string[]) => void, assetIds?: string[], externalCurrentList?: OccurrenceWithTimeSpent[], externalFinalizedList?: OccurrenceWithTimeSpent[]) {
    const { allAssets } = useAssets();

    const [showIndicators, setShowIndicators] = useState(false);
    const [loading, setLoading] = useState(true);

    const [selectedMetric, setSelectedMetric] = useState<"average" | "quantity">("average");

    const [allOccurrenceIdList, setAllOccurrenceIdList] = useState<string[]>([]);
    const [allOccurrenceTimeSpentList, setAllOccurrenceTimeSpentList] = useState<{ occurrenceId: string, timeSpent: number, chassis: string, steps: OccurrenceWithTimeSpent['steps'], occurrenceType?: string }[]>([]);
    const [filteredOccurrenceIdList, setFilteredOccurrenceIdList] = useState<string[]>([]);

    const [inProgress, setInProgress] = useState<string | undefined>(undefined);
    const [inProgressIndicators, setInProgressIndicators] = useState<{ name: string, stepKey: OccurrenceStepEnum, qtd: number, averageTime: number, occurrenceIds: string[], allOccurrenceIds: string[], chassisList: string[] }[]>([]);
    const [inProgressTotals, setInProgressTotals] = useState<IInProgressIndicators>({ finishedQtd: 0, finishedAverageTime: 0, inProgressQtd: 0, inProgressAverageTime: 0, occurrenceIds: [], chassisList: [] });

    const [customer, setCustomer] = useState<string | undefined>(undefined);
    const [customerIndicators, setCustomerIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [model, setModel] = useState<string | undefined>(undefined);
    const [modelIndicators, setModelIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [emissionStandard, setEmissionStandard] = useState<string | undefined>(undefined);
    const [emissionStandardIndicators, setEmissionStandardIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [state, setState] = useState<string | undefined>(undefined);
    const [stateIndicators, setStateIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [city, setCity] = useState<string | undefined>(undefined);
    const [cityIndicators, setCityIndicators] = useState<{ name: string, qtd: number, averageTime: number, occurrenceIds: string[], chassisList: string[] }[]>([]);

    const [groupIndicators, setGroupIndicators] = useState<{ group: string, items: { name: string, qtd: number, averageTime: number, chassisList: string[], occurrenceIds: string[] }[], total: number, averageTime: number }[]>([]);

    const processData = (occurrenceWithTimeSpentList: OccurrenceWithTimeSpent[], finalizedOccurrenceWithTimeSpentList: OccurrenceWithTimeSpent[]) => {
        setLoading(true);

        setAllOccurrenceTimeSpentList(occurrenceWithTimeSpentList
            .map(el => ({ occurrenceId: el.occurrenceId, timeSpent: el.timeSpent, chassis: el.chassis, steps: el.steps, occurrenceType: el.occurrenceType })));

        const getTimeSpentByChassis = (occurrences: OccurrenceWithTimeSpent[]) => {
            const map = new Map<string, number>();
            occurrences.forEach(item => map.set(item.chassis, (map.get(item.chassis) ?? 0) + item.timeSpent));
            return Array.from(map.values());
        };

        const getAverageByChassis = (occurrences: OccurrenceWithTimeSpent[], getTime: (occ: OccurrenceWithTimeSpent) => number) => {
            const map = new Map<string, number>();
            occurrences.forEach(el => map.set(el.chassis, (map.get(el.chassis) ?? 0) + getTime(el)));
            const values = Array.from(map.values());
            return values.length > 0 ? values.reduce((acc, t) => acc + t, 0) / values.length : 0;
        };

        const isClaimType = (el: OccurrenceWithTimeSpent) => el.occurrenceType === 'CL' || el.occurrenceType === 'CLAIM';
        const currentForTime = occurrenceWithTimeSpentList.filter(el => !isClaimType(el));
        const finalizedForTime = finalizedOccurrenceWithTimeSpentList.filter(el => !isClaimType(el));

        const customers = [...new Set(occurrenceWithTimeSpentList.map(el => el.customer))];
        const models = [...new Set(occurrenceWithTimeSpentList.map(el => el.model))];
        const emissionStandards = [...new Set(occurrenceWithTimeSpentList.map(el => el.legislation))];
        const states = [...new Set(occurrenceWithTimeSpentList.map(el => el.state))];
        const cities = [...new Set(occurrenceWithTimeSpentList.map(el => el.city))];

        setCustomerIndicators(customers.map(customer => {
            const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.customer === customer);
            const occurrencesFoundForTime = occurrencesFound.filter(el => !isClaimType(el));
            return { name: customer, qtd: occurrencesFound.length, averageTime: getAverageByChassis(occurrencesFoundForTime, el => el.timeSpent), occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
        }));
        setModelIndicators(models.map(model => {
            const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.model === model);
            const occurrencesFoundForTime = occurrencesFound.filter(el => !isClaimType(el));
            return { name: model, qtd: occurrencesFound.length, averageTime: getAverageByChassis(occurrencesFoundForTime, el => el.timeSpent), occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
        }));
        setEmissionStandardIndicators(emissionStandards.map(emissionStandard => {
            const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.legislation === emissionStandard);
            const occurrencesFoundForTime = occurrencesFound.filter(el => !isClaimType(el));
            return { name: emissionStandard, qtd: occurrencesFound.length, averageTime: getAverageByChassis(occurrencesFoundForTime, el => el.timeSpent), occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
        }));
        setStateIndicators(states.map(state => {
            const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.state === state);
            const occurrencesFoundForTime = occurrencesFound.filter(el => !isClaimType(el));
            return { name: state, qtd: occurrencesFound.length, averageTime: getAverageByChassis(occurrencesFoundForTime, el => el.timeSpent), occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
        }));
        setCityIndicators(cities.map(city => {
            const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.city === city);
            const occurrencesFoundForTime = occurrencesFound.filter(el => !isClaimType(el));
            return { name: city, qtd: occurrencesFound.length, averageTime: getAverageByChassis(occurrencesFoundForTime, el => el.timeSpent), occurrenceIds: occurrencesFound.map(el => el.occurrenceId), chassisList: [] };
        }));

        const inProgressTimeSpentByChassis = getTimeSpentByChassis(currentForTime);
        const finalizedTimeSpentByChassis = getTimeSpentByChassis(finalizedForTime);
        const inProgressChassisForTime = new Set(currentForTime.map(el => el.chassis)).size;
        const finalizedChassisForTime = new Set(finalizedForTime.map(el => el.chassis)).size;

        setInProgressTotals({
            finishedQtd: finalizedOccurrenceWithTimeSpentList.length,
            finishedAverageTime: finalizedChassisForTime > 0
                ? finalizedTimeSpentByChassis.reduce((acc, t) => acc + t, 0) / finalizedChassisForTime
                : 0,
            inProgressQtd: occurrenceWithTimeSpentList.length,
            inProgressAverageTime: inProgressChassisForTime > 0
                ? inProgressTimeSpentByChassis.reduce((acc, t) => acc + t, 0) / inProgressChassisForTime
                : 0,
            occurrenceIds: occurrenceWithTimeSpentList.map(el => el.occurrenceId),
            chassisList: [],
        })

        const uniqueDealershipList = [...new Set(occurrenceWithTimeSpentList.map(el => el.dealershipName))];
        const dealershipGroupDns = uniqueDealershipList
            .map(name => ({ name, dns: [...new Set(occurrenceWithTimeSpentList.filter(el => el.dealershipName === name).map(el => el.dn))] }));

        setGroupIndicators(uniqueDealershipList.map(dealership => {
            const dealershipGroupDnsFound = dealershipGroupDns.find(el => el.name === dealership) ?? { name: dealership, dns: [] };
            return {
                group: dealership,
                items: dealershipGroupDnsFound.dns
                    .map(dn => {
                        const occurrencesByDnFound = occurrenceWithTimeSpentList.filter(el => el.dn === dn);
                        const occurrencesByDnForTime = occurrencesByDnFound.filter(el => !isClaimType(el));
                        return {
                            name: `${dealership} - ${dn}`,
                            qtd: occurrencesByDnFound.length,
                            averageTime: getAverageByChassis(occurrencesByDnForTime, el => el.timeSpent),
                            occurrenceIds: occurrencesByDnFound.map(el => el.occurrenceId),
                            chassisList: [],
                        };
                    }),
                total: 0,
                averageTime: 0,
            }
        }));

        setInProgressIndicators(Object.values(OccurrenceStepEnum)
            .map(key => {
                // Quantidade: todos que passaram pela etapa (inclui CL/CLAIM)
                const allStepOccurrences = occurrenceWithTimeSpentList.filter(el => el.steps[key]);
                // Tempo: exclui sinistros — mesmo padrão do kanban
                const allStepOccurrencesForTime = allStepOccurrences.filter(el => !isClaimType(el));
                return {
                    name: getOccurrenceStepLabelByStepValue(key),
                    stepKey: key,
                    qtd: allStepOccurrences.length,
                    averageTime: getAverageByChassis(allStepOccurrencesForTime, el => el.steps[key]?.total || 0),
                    occurrenceIds: allStepOccurrences.map(el => el.occurrenceId),
                    allOccurrenceIds: allStepOccurrences.map(el => el.occurrenceId),
                    chassisList: [...new Set(allStepOccurrences.map(el => el.chassis))]
                }
            }));

        // Get all occurrence ids using customer
        const allOccurrenceIds = customers.flatMap(customer => {
            const occurrencesFound = occurrenceWithTimeSpentList.filter(el => el.customer === customer);
            return occurrencesFound.map(el => el.occurrenceId);
        })

        setAllOccurrenceIdList(allOccurrenceIds);
        setFilteredOccurrenceIdList(allOccurrenceIds);

        setLoading(false);
    }

    useEffect(() => {
        if (externalCurrentList !== undefined && externalFinalizedList !== undefined) {
            processData(externalCurrentList, externalFinalizedList);
        }
    }, [externalCurrentList, externalFinalizedList]);

    useEffect(() => {
        if (externalCurrentList !== undefined) return;

        const fetchData = async (chassisListFilter: string[]) => {
            const allOccurrencesWithTimeSpent = await fetchOccurenceListWithTimeSpentForIndicators({ chassisListFilter }, persona);
            processData(allOccurrencesWithTimeSpent.current, allOccurrencesWithTimeSpent.finalized);
        }

        if (assetIds && assetIds.length > 0) {
            fetchData(
                ['CONSULTANT', 'PARTNERSHIP'].includes(persona) ? [] :
                    getChassisListFromAssetIds(allAssets, assetIds)
            );
        }
    }, [assetIds]);

    const filterCharts = (chart: "inProgress" | "customer" | "model" | "emissionStandard" | "state" | "city", value: string) => {
        let localFilteredOccurrenceIdList: string[] = [];
        // let localFilteredChassisList: string[] = [];
        if (chart === "inProgress") {
            setInProgress(value === inProgress ? undefined : value);
            const step = inProgressIndicators.find(el => el.name === value);
            localFilteredOccurrenceIdList = value === inProgress ? allOccurrenceIdList : (step?.allOccurrenceIds || []);
            // localFilteredChassisList = value === inProgress ? allOccurrenceIdList : ((inProgressIndicators.find(el => el.name === value)?.chassisList) || []);
            setCustomer(undefined);
            setModel(undefined);
            setEmissionStandard(undefined);
            setState(undefined);
            setCity(undefined);
        }
        if (chart === "customer") {
            setInProgress(undefined);
            setCustomer(value === customer ? undefined : value);
            localFilteredOccurrenceIdList = value === customer ? allOccurrenceIdList : (customerIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === customer ? allOccurrenceIdList : (customerIndicators.find(el => el.name === value)?.chassisList || []);
            setModel(undefined);
            setEmissionStandard(undefined);
            setState(undefined);
            setCity(undefined);
        }
        if (chart === "model") {
            setInProgress(undefined);
            setModel(value === model ? undefined : value);
            localFilteredOccurrenceIdList = value === model ? allOccurrenceIdList : (modelIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === model ? allOccurrenceIdList : (modelIndicators.find(el => el.name === value)?.chassisList || []);
            setCustomer(undefined);
            setEmissionStandard(undefined);
            setState(undefined);
            setCity(undefined);
        }
        if (chart === "emissionStandard") {
            setInProgress(undefined);
            setEmissionStandard(value === emissionStandard ? undefined : value);
            localFilteredOccurrenceIdList = value === emissionStandard ? allOccurrenceIdList : (emissionStandardIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === emissionStandard ? allOccurrenceIdList : (emissionStandardIndicators.find(el => el.name === value)?.chassisList || []);
            setCustomer(undefined);
            setModel(undefined);
            setState(undefined);
            setCity(undefined);
        }
        if (chart === "state") {
            setInProgress(undefined);
            setState(value === state ? undefined : value);
            localFilteredOccurrenceIdList = value === state ? allOccurrenceIdList : (stateIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === state ? allOccurrenceIdList : (stateIndicators.find(el => el.name === value)?.chassisList || []);
            setCustomer(undefined);
            setModel(undefined);
            setEmissionStandard(undefined);
            setCity(undefined);
        }
        if (chart === "city") {
            setInProgress(undefined);
            setCity(value === city ? undefined : value);
            localFilteredOccurrenceIdList = value === city ? allOccurrenceIdList : (cityIndicators.find(el => el.name === value)?.occurrenceIds || []);
            // localFilteredChassisList = value === city ? allOccurrenceIdList : (cityIndicators.find(el => el.name === value)?.chassisList || []);
            setCustomer(undefined);
            setModel(undefined);
            setEmissionStandard(undefined);
            setState(undefined);
        }

        setFilteredOccurrenceIdList(localFilteredOccurrenceIdList);
        // setChassisList(localFilteredChassisList);
    }

    const getInProgressTotals = (): { totals: number, averageTime: number } => {
        const filteredItems = allOccurrenceTimeSpentList.filter(el => filteredOccurrenceIdList.includes(el.occurrenceId));
        const totals = filteredItems.length;

        const filteredItemsForTime = filteredItems.filter(el => el.occurrenceType !== 'CL' && el.occurrenceType !== 'CLAIM');
        const chassisMap = new Map<string, number>();
        filteredItemsForTime.forEach(el => {
            chassisMap.set(el.chassis, (chassisMap.get(el.chassis) ?? 0) + el.timeSpent);
        });
        const chassisTimeSpents = Array.from(chassisMap.values());
        const averageTime = chassisTimeSpents.length > 0
            ? chassisTimeSpents.reduce((acc, t) => acc + t, 0) / chassisTimeSpents.length
            : 0;

        return {
            totals,
            averageTime,
        }
    }

    return {
        loading,
        showIndicators,
        setShowIndicators,

        inProgressIndicators,
        inProgressTotals,

        selectedMetric,
        setSelectedMetric,

        inProgress,
        customer,
        model,
        emissionStandard,
        state,
        city,

        customerIndicators,
        modelIndicators,
        emissionStandardIndicators,
        stateIndicators,
        cityIndicators,
        groupIndicators,

        filterCharts,
        filteredOccurrenceIdList,

        getInProgressTotals,
        allOccurrenceTimeSpentList,
    }
}

export default useIndicators;